import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { formatDateTimeIT } from "@/lib/formatters";
import { backendUrl } from "@/lib/backendUrl";

function formatValue(value, fallback = "Non disponibile") {
  if (value === undefined || value === null || String(value).trim() === "") {
    return fallback;
  }
  return String(value);
}

function mapRecoveryActionResult(status, payload) {
  const message = typeof payload?.message === "string" ? payload.message : "";

  if (status === 200 && message === "Slot confirmed and scheduled.") {
    return {
      title: "Proposta accettata",
      message: "La nuova prenotazione è stata registrata correttamente.",
      tone: "success",
    };
  }

  return {
    title: "Proposta non disponibile",
    message: "Questo slot non è più disponibile o è già stato utilizzato.",
    tone: "warning",
  };
}

export function CustomerRecoveryConfirmPage() {
  const { token } = useParams();
  const [preview, setPreview] = useState(null);
  const [pageState, setPageState] = useState(token ? "loading" : "error");
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let active = true;

    const loadPreview = async () => {
      if (!token) {
        setPageState("error");
        return;
      }

      setPageState("loading");
      try {
        const response = await fetch(
          `${backendUrl}/api/public/recovery/confirm/${encodeURIComponent(token)}/preview`
        );
        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!active) {
          return;
        }

        if (response.status === 404 || !payload?.slot_appointment || !payload?.candidate_appointment) {
          setPageState("invalid");
          return;
        }

        setPreview(payload);
        setPageState("ready");
      } catch (error) {
        if (active) {
          setPageState("error");
        }
      }
    };

    loadPreview();
    return () => {
      active = false;
    };
  }, [token]);

  const confirmRecovery = async () => {
    if (!token || pageState === "submitting") {
      return;
    }

    setPageState("submitting");
    try {
      const response = await fetch(
        `${backendUrl}/api/public/recovery/confirm/${encodeURIComponent(token)}/action`,
        { method: "POST" }
      );
      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }
      setFeedback(mapRecoveryActionResult(response.status, payload));
      setPageState("result");
    } catch (error) {
      setPageState("error");
    }
  };

  const toneClass =
    feedback?.tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : pageState === "ready" && preview?.action_available
        ? "border-slate-200 bg-slate-50 text-slate-700"
        : "border-amber-200 bg-amber-50 text-amber-700";

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 flex items-center justify-center">
      <section className="w-full max-w-xl bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">FullBook Customer App</p>
          <p className="mt-4 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
            Proposta di recupero
          </p>
        </div>

        <div className={`mb-5 flex h-11 w-11 items-center justify-center rounded-full border ${toneClass}`}>
          {pageState === "loading" || pageState === "submitting" ? "..." : feedback?.tone === "success" ? "OK" : "!"}
        </div>

        {(pageState === "loading" || pageState === "submitting") && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">Proposta di recupero</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {pageState === "loading"
                ? "Stiamo caricando i dettagli della proposta."
                : "Stiamo registrando la tua accettazione."}
            </p>
          </>
        )}

        {pageState === "invalid" && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">Link non valido o scaduto</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Non è possibile aprire questa proposta da questo link.
            </p>
          </>
        )}

        {pageState === "error" && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">Errore temporaneo</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Non siamo riusciti a caricare questa proposta. Riprova più tardi.
            </p>
          </>
        )}

        {pageState === "result" && feedback && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">{feedback.title}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{feedback.message}</p>
          </>
        )}

        {pageState === "ready" && preview && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">Valuta il nuovo slot</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{preview.status_message}</p>

            <div className="mt-6 rounded-sm border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-4 text-sm md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Servizio</p>
                  <p className="mt-1 text-slate-900">
                    {formatValue(preview.candidate_appointment.service_name)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Nuovo stato</p>
                  <p className="mt-1 text-slate-900">
                    {formatValue(preview.candidate_appointment.status)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Nuovo inizio</p>
                  <p className="mt-1 text-slate-900">
                    {formatDateTimeIT(preview.candidate_appointment.start_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Nuova fine</p>
                  <p className="mt-1 text-slate-900">
                    {formatDateTimeIT(preview.candidate_appointment.end_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Staff</p>
                  <p className="mt-1 text-slate-900">
                    {formatValue(preview.slot_appointment.staff_name)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Scadenza proposta</p>
                  <p className="mt-1 text-slate-900">{formatDateTimeIT(preview.expires_at)}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={confirmRecovery}
                disabled={!preview.action_available}
                className="h-11 flex-1 rounded-sm bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-50"
              >
                Accetta proposta
              </button>
              <button
                type="button"
                onClick={() => window.close()}
                className="h-11 flex-1 rounded-sm border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700"
              >
                Chiudi pagina
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}
